// src/components/UserEditModal.js
// Updated: 01/13/2026 10:45 PM ET

import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, TextField, Button, Select, MenuItem,
  InputLabel, FormControl, FormControlLabel, Switch, CircularProgress,
  Tabs, Tab, Divider, InputAdornment, Alert
} from '@mui/material';
import { Link as LinkIcon } from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
// Import Firebase Functions utilities
import { getFunctions, httpsCallable } from 'firebase/functions';
import { v4 as uuidv4 } from 'uuid';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function UserEditModal({
  open, onClose, onSave, onDelete, user,
  adminChurchId, editorProfile, allChurches,
  isChurchPushNotificationsEnabled,
}) {
  const [userData, setUserData] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchUserData = async () => {
      if (open && user) {
        const initialData = { ...user };
        // Existing Flags
        initialData.canSendNotifications = !!user.canSendNotifications;
        initialData.canViewPrivatePrayers = !!user.canViewPrivatePrayers;
        initialData.canEditHomePage = !!user.canEditHomePage;
        initialData.canViewInquiries = !!user.canViewInquiries;

        // New Financial Fields (Initialize if missing)
        initialData.householdId = user.householdId || '';
        initialData.financeRole = user.financeRole || ''; // Default to empty (no access)
        initialData.financialIdentifiers = user.financialIdentifiers || {
          envelopeNumber: '',
          cashAppTag: '',
          zelleID: ''
        };

        const privateContactInfoDocRef = doc(db, 'users', user.id, 'privateContactInfo', 'details');
        try {
          const privateDocSnap = await getDoc(privateContactInfoDocRef);
          if (privateDocSnap.exists()) {
            const privateData = privateDocSnap.data();
            initialData.email = privateData.email || '';
            initialData.phone = privateData.phone || '';
            initialData.address = privateData.address || '';
            initialData.city = privateData.city || '';
            initialData.state = privateData.state || '';
            initialData.zip = privateData.zip || '';
            initialData.birthday = privateData.birthday ? privateData.birthday.toDate() : null;
          } else {
            // Defaults
            initialData.email = ''; initialData.phone = ''; initialData.address = '';
            initialData.city = ''; initialData.state = ''; initialData.zip = '';
            initialData.birthday = null;
          }
        } catch (error) {
          console.error("Error fetching private contact info for user:", user.id, error);
          // Fallbacks on error
          initialData.email = ''; initialData.phone = ''; initialData.address = '';
          initialData.city = ''; initialData.state = ''; initialData.zip = '';
          initialData.birthday = null;
        }
        setUserData(initialData);
        setTabValue(0); // Reset to Profile tab on open
      }
    };

    fetchUserData();
  }, [user, open, adminChurchId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFinancialChange = (e) => {
    const { name, value } = e.target;
    setUserData(prevState => ({
      ...prevState,
      financialIdentifiers: {
        ...prevState.financialIdentifiers,
        [name]: value
      }
    }));
  };

  const generateNewHousehold = () => {
    const newId = uuidv4();
    setUserData(prevState => ({ ...prevState, householdId: newId }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleApproveAndSave = () => {
    const finalUserData = {
      ...userData,
      status: 'active',
    };
    onSave(finalUserData);
  };

  const handleDeny = () => {
    if (onDelete && userData.id) {
        onDelete(userData.id);
    }
  };

  const handleDetach = async () => {
    if (!user || user.status !== 'active') {
      alert("This action is only available for active users.");
      return;
    }
    if (window.confirm(`Are you sure you want to detach ${user.firstName} ${user.lastName} from the church? They will be logged out and will need to join a new church.`)) {
      try {
        const functions = getFunctions();
        const detachUser = httpsCallable(functions, 'detachUserFromChurch');
        await detachUser({ userIdToDetach: user.id, churchId: user.churchId });
        
        alert('User has been detached successfully.');
        onClose();
      } catch (error) {
        console.error("Error detaching user:", error);
        alert(`Failed to detach user: ${error.message}`);
      }
    }
  };
  
  const handleMarkEmailAsVerified = async () => {
      if (!user || !user.id) return;
      if (!window.confirm(`Are you sure you want to manually mark ${userData.email} as verified?`)) return;

      setIsVerifying(true);
      try {
          const functions = getFunctions();
          const markEmailAsVerified = httpsCallable(functions, 'markEmailAsVerified');
          await markEmailAsVerified({ userId: user.id });

          alert(`Email for ${userData.firstName} ${userData.lastName} has been marked as verified.`);
          onClose();
      } catch (error) {
          console.error("Error marking email as verified:", error);
          alert(`Failed to mark email as verified: ${error.message}`);
      } finally {
          setIsVerifying(false);
      }
  };

  // Permissions Logic
  const canAssignSuperAdmin = editorProfile?.role === 'Super Administrator';
  const isSuperAdminEditing = editorProfile?.role === 'Super Administrator';
  const isChurchAdmin = editorProfile?.role === 'Church Administrator';
  
  const canSeeNotificationSwitch = isChurchPushNotificationsEnabled && (isChurchAdmin || isSuperAdminEditing);
  const canSeePrivatePrayerSwitch = isChurchAdmin || isSuperAdminEditing;
  const canSeeEditHomePageSwitch = isChurchAdmin || isSuperAdminEditing;
  const canSeeViewInquiriesSwitch = isChurchAdmin || isSuperAdminEditing;
  
  const isChurchAdminEditingOwnProfile = isChurchAdmin && editorProfile?.authUid === user?.id;

  // New: Define who can see the Financial Tab
  // Only Super Admins or specific Finance Roles can see this.
  const canManageFinancials = isSuperAdminEditing || 
                              ['admin', 'counter'].includes(editorProfile?.financeRole);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" gutterBottom>
          Edit User: {userData.firstName} {userData.lastName}
        </Typography>

        <Tabs value={tabValue} onChange={handleTabChange} aria-label="user edit tabs" variant="fullWidth" sx={{ mb: 2 }}>
          <Tab label="Profile & Roles" />
          {canManageFinancials && <Tab label="Financial" />} {/* Removed '& Household' from label */}
        </Tabs>

        {/* TAB 0: PROFILE & ROLES */}
        <TabPanel value={tabValue} index={0}>
          <TextField margin="normal" fullWidth label="First Name" name="firstName" value={userData.firstName || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Last Name" name="lastName" value={userData.lastName || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Email" name="email" value={userData.email || ''} onChange={handleChange} />
          
          {isSuperAdminEditing && userData.email && !userData.emailVerified && (
            <Button
                variant="outlined"
                onClick={handleMarkEmailAsVerified}
                disabled={isVerifying}
                sx={{ mt: 2 }}
            >
                {isVerifying ? <CircularProgress size={24} /> : 'Mark Email as Verified'}
            </Button>
          )}

          <TextField margin="normal" fullWidth label="Phone Number" name="phone" value={userData.phone || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Street Address" name="address" value={userData.address || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="City" name="city" value={userData.city || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="State" name="state" value={userData.state || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Zip Code" name="zip" value={userData.zip || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Birthday" name="birthday" type="date"
                    value={userData.birthday ? new Date(userData.birthday).toISOString().split('T')[0] : ''}
                    onChange={handleChange} InputLabelProps={{ shrink: true }} />

          <FormControl fullWidth margin="normal">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              name="role"
              value={userData.role || 'Member'}
              label="Role"
              onChange={handleChange}
              disabled={isChurchAdminEditingOwnProfile}
            >
              <MenuItem value="Member">Member</MenuItem>
              <MenuItem value="Leadership">Leadership</MenuItem>
              <MenuItem value="Church Administrator">Church Administrator</MenuItem>
              {canAssignSuperAdmin && (
                <MenuItem value="Super Administrator">Super Administrator</MenuItem>
              )}
            </Select>
          </FormControl>

          {canSeeViewInquiriesSwitch && (
            <FormControlLabel
                control={
                    <Switch
                        checked={userData.canViewInquiries || false}
                        onChange={handleChange}
                        name="canViewInquiries"
                        disabled={isChurchAdminEditingOwnProfile}
                    />
                }
                label="Can View Member Inquiries"
            />
          )}

          {canSeeNotificationSwitch && (
            <FormControlLabel
                control={
                    <Switch
                        checked={userData.canSendNotifications || false}
                        onChange={handleChange}
                        name="canSendNotifications"
                        disabled={isChurchAdminEditingOwnProfile}
                    />
                }
                label="Can Send Push Notifications"
            />
          )}

          {canSeePrivatePrayerSwitch && (
            <FormControlLabel
                control={
                    <Switch
                        checked={userData.canViewPrivatePrayers || false}
                        onChange={handleChange}
                        name="canViewPrivatePrayers"
                        disabled={isChurchAdminEditingOwnProfile}
                    />
                }
                label="Can View Private Prayers"
            />
          )}

          {canSeeEditHomePageSwitch && (
            <FormControlLabel
                control={
                    <Switch
                        checked={userData.canEditHomePage || false}
                        onChange={handleChange}
                        name="canEditHomePage"
                        disabled={isChurchAdminEditingOwnProfile}
                    />
                }
                label="Can Edit Home Page"
            />
          )}

          {isSuperAdminEditing ? (
            <FormControl fullWidth margin="normal">
              <InputLabel id="church-select-label">Assigned Church</InputLabel>
              <Select
                labelId="church-select-label"
                name="churchId"
                value={userData.churchId || ''}
                label="Assigned Church"
                onChange={handleChange}
              >
                <MenuItem value=""><em>None (Pending Approval)</em></MenuItem>
                {allChurches.map((church) => (
                  <MenuItem key={church.id} value={church.id}>
                    {church.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              margin="normal"
              fullWidth
              label="Church ID"
              name="churchId"
              value={userData.churchId || ''}
              onChange={handleChange}
              disabled={true}
              helperText={!user?.churchId ? "Assigning to your church." : "Church already assigned."}
            />
          )}
        </TabPanel>

        {/* TAB 1: FINANCIAL */}
        {canManageFinancials && (
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom sx={{ mt: 1, fontSize: '1rem' }}>
              Finance Role & Permissions
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Assigning a role below will grant access to the Financial Stewardship Module.
            </Typography>

            <FormControl fullWidth margin="normal" sx={{ mb: 4 }}>
              <InputLabel id="finance-role-label">Financial System Role</InputLabel>
              <Select
                labelId="finance-role-label"
                name="financeRole"
                value={userData.financeRole || ''}
                label="Financial System Role"
                onChange={handleChange}
              >
                <MenuItem value=""><em>None (No Access)</em></MenuItem>
                <MenuItem value="viewer">Viewer (Read-Only Reports)</MenuItem>
                <MenuItem value="counter">Counter (Enter Batches)</MenuItem>
                <MenuItem value="admin">Finance Admin (Treasurer)</MenuItem>
              </Select>
            </FormControl>

            {/* HOUSEHOLD SECTION HIDDEN PER USER REQUEST
            <Divider sx={{ my: 2 }} />
            ... (Household code commented out) ...
            */}

            {/* FINANCIAL IDENTIFIERS MOVED TO FINANCE MODULE PER USER REQUEST */}
            {/*
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">FINANCIAL IDENTIFIERS</Typography>
            </Divider>

            <TextField
              margin="normal"
              fullWidth
              label="Envelope Number"
              name="envelopeNumber"
              value={userData.financialIdentifiers?.envelopeNumber || ''}
              onChange={handleFinancialChange}
            />
            <TextField
              margin="normal"
              fullWidth
              label="CashApp Tag"
              name="cashAppTag"
              value={userData.financialIdentifiers?.cashAppTag || ''}
              onChange={handleFinancialChange}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Zelle ID / Phone"
              name="zelleID"
              value={userData.financialIdentifiers?.zelleID || ''}
              onChange={handleFinancialChange}
            />
            */}
            
            <Alert severity="info" sx={{ mt: 2 }}>
                Financial Identifiers (Envelope #, CashApp, etc.) and Households are now managed in the <strong>Donor Management</strong> tab within the Financials module.
            </Alert>
          </TabPanel>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Box>
                {userData.status === 'pending_approval' ? (
                    <Button onClick={handleDeny} variant="outlined" color="error">
                        Deny & Delete User
                    </Button>
                ) : (
                    <Button onClick={handleDetach} variant="outlined" color="warning">
                        Detach from Church
                    </Button>
                )}
            </Box>

            <Box>
                <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
                <Button onClick={handleApproveAndSave} variant="contained">
                    {userData.status === 'pending_approval' ? 'Approve & Save' : 'Save Changes'}
                </Button>
            </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default UserEditModal;